import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Radio, RadioChangeEvent } from 'antd';
import { formatBRNumber } from '@utils/formatters';
import useSwr from '@hooks/useSwr';
import getPercentage from '@utils/percentage';
import { valueType } from 'antd/lib/statistic/utils';
import SessionContext from '@providers/Session/SessionContext';
import StatCard from '@components/StatCard';

interface StatusLinesProps {
  days?: number;
  onClick: (days: number) => void;
  filters: any;
  allowMultiple?: boolean;
}

const ALL_DAYS = [30, 60, 90];

const StatusLines = ({
  onClick,
  filters,
  allowMultiple,
  days = ALL_DAYS[0],
}: StatusLinesProps) => {
  const { t } = useTranslation('dashboard');
  const { selectedApp, isMot } = useContext(SessionContext);
  const routeToFindData = isMot
    ? 'MOT'
    : selectedApp?.split('_').join('').toUpperCase();

  const [selectedDays, setSelectedDays] = useState<number>(ALL_DAYS[0]);

  const { data } = useSwr(
    `/service-proxy/inventory/${routeToFindData}/dashboards/count-by-seen-in-last-days`,
    {
      params: { days: allowMultiple ? selectedDays : days },
      method: 'post',
      data: {
        ...filters,
      },
    },
  );

  const formatStatisticValue = useCallback(
    (value: valueType) => formatBRNumber(value),
    [],
  );

  const onRadioChange = useCallback((e: RadioChangeEvent) => {
    const { value } = e.target;
    setSelectedDays(value);
  }, []);

  const statisticTitle = useMemo(() => {
    if (allowMultiple) {
      return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span>{t('status-lines.seen-there')}</span>
          <Radio.Group
            size="small"
            onChange={onRadioChange}
            value={selectedDays}
            style={{ marginLeft: 8 }}
          >
            {ALL_DAYS.map(item => (
              <Radio.Button
                key={item}
                value={item}
                style={{ padding: 0, zIndex: 0 }}
              >
                <Button
                  size="small"
                  type="link"
                  style={{
                    margin: 0,
                    color: 'inherit',
                    opacity: selectedDays !== item ? 0.4 : 1,
                  }}
                  onClick={e => {
                    e.stopPropagation();
                    setSelectedDays(item);
                  }}
                >
                  {selectedDays === item
                    ? t('status-lines.days', { count: item })
                    : item}
                </Button>
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>
      );
    }
    return t('status-lines.connection_days', { count: days });
  }, [allowMultiple, days, onRadioChange, selectedDays, t]);

  const percentage = useMemo(
    () => (data ? `(${getPercentage(data?.matching, data?.total, 2)}%)` : 0),
    [data],
  );

  return (
    <StatCard
      loading={!data}
      noData={data?.length === 0 || data?.total === 0}
      onClick={() => {
        onClick(allowMultiple ? selectedDays : days);
      }}
      precision={0}
      suffix={percentage}
      title={statisticTitle}
      value={data?.matching}
      formatter={formatStatisticValue}
    />
  );
};

export default StatusLines;
